// TODO: figure out how to override Checkbox/CheckboxGroup class type definition to support `onBlur` prop
// @ts-nocheck
import { Checkbox as $Checkbox, Row, Col } from 'antd';
import {
  CheckboxChangeEvent as $CheckboxChangeEvent,
  CheckboxGroupProps as $CheckboxGroupProps,
  CheckboxProps as $CheckboxProps,
} from 'antd/lib/checkbox';
import { CheckboxValueType, CheckboxOptionType } from 'antd/lib/checkbox/Group';
import React, { useRef } from 'react';

import { OnBlurCallback, OnChangeCallback, FormElementChangeEvent, CheckboxChangeEvent } from './index';

const ALL_CHECKBOX_VALUE = '__ALL_CHECKBOX_VALUE__';
const ALL_CHECKBOX_LABEL = 'All of the above';

const getOptionValue = (option: string | CheckboxOptionType) => (typeof option === 'string' ? option : option.value);
const getOptionLabel = (option: string | CheckboxOptionType) => (typeof option === 'string' ? option : option.label);

export type CheckboxProps = Omit<$CheckboxProps, 'onChange'> & {
  onBlur?: (event: CheckboxChangeEvent) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: CheckboxChangeEvent) => void;
};
export type CheckboxGroupProps = Omit<$CheckboxGroupProps, 'onChange'> & {
  hasCheckAll?: boolean;
  onBlur?: OnBlurCallback;
  onChange?: OnChangeCallback;
};

export const CheckboxGroup = React.memo(function CheckboxGroup({
  name,
  value: valueProp,
  options: optionsProp,
  hasCheckAll = false,
  onBlur,
  onChange,
  ...rest
}: CheckboxGroupProps) {
  const isAllCheckboxChecked = useRef(false);
  const isFocused = useRef(false);
  const handleFocus =
    onBlur &&
    (() => {
      isFocused.current = true;
    });
  const handleBlur =
    onBlur &&
    (() => {
      // check if the current checkbox lost the focus because another one in the same group gained the focus,
      // then we consider the checkboxgroup itself still has focus
      setTimeout(() => {
        if (!isFocused.current) {
          const values = Array.from(
            document.querySelectorAll<HTMLInputElement>(`input[type="checkbox"][name="${name}"]`)
          )
            .filter((checkbox: HTMLInputElement) => checkbox.checked)
            .map(checkbox => checkbox.value);
          onBlur({ name, value: values });
        }
      }, 16);
      isFocused.current = false;
    });
  const handleChange =
    onChange &&
    ((checkedValue: CheckboxValueType[]) => {
      /**
       * Uncheck "All of the above" checkbox when all the checkboxes are checked and when unchecking
       * any of the other checkboxes.
       * @example Uncheck "All of the above" checkbox when "Football" is unchecked
       *  [x] Football
       *  [x] Basketball
       *  [x] All of the above
       */
      if (isAllCheckboxChecked.current && checkedValue.length === valueProp?.length) {
        isAllCheckboxChecked.current = false;
      }

      let value;
      if (isAllCheckboxChecked.current) {
        value = optionsProp?.map(option => getOptionValue(option));
      } else {
        value = checkedValue.filter(optionValue => optionValue !== ALL_CHECKBOX_VALUE);
      }
      onChange({ name, value });
    });
  const handleCheckAllChange = ({ value }: FormElementChangeEvent) => {
    isAllCheckboxChecked.current = value;
  };

  /**
   * Add "ALL_CHECKBOX_VALUE" to the value to mark the all checkbox checked when all the checkboxes are checked
   */
  const value = Array.isArray(valueProp) ? [...valueProp] : [];
  if (isAllCheckboxChecked.current) {
    value.push(ALL_CHECKBOX_VALUE);
  }

  return (
    <$Checkbox.Group
      {...rest}
      name={name}
      value={value}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onChange={handleChange}
    >
      {optionsProp?.map((option, key) => {
        const optionValue = getOptionValue(option);
        const optionLabel = getOptionLabel(option);
        const checked = Array.isArray(value) ? value.includes(optionValue) : false;
        return (
          <Row key={key}>
            <Col>
              <Checkbox value={optionValue} checked={checked}>
                {optionLabel}
              </Checkbox>
            </Col>
          </Row>
        );
      })}
      {hasCheckAll && (
        <Row>
          <Col>
            <Checkbox value={ALL_CHECKBOX_VALUE} onChange={handleCheckAllChange}>
              {ALL_CHECKBOX_LABEL}
            </Checkbox>
          </Col>
        </Row>
      )}
    </$Checkbox.Group>
  );
});

const Checkbox = React.memo(function Checkbox({ name, onBlur, onChange, ...rest }: CheckboxProps) {
  const handleBlur =
    onBlur &&
    ((event: React.FocusEvent<HTMLInputElement>) => {
      const {
        target: { checked },
      } = event;
      onBlur({ name, value: checked, checked });
    });
  const handleChange =
    onChange &&
    ((event: $CheckboxChangeEvent) => {
      const {
        target: { checked },
      } = event;
      onChange({ name, value: checked, checked });
    });
  return <$Checkbox {...rest} name={name} onBlur={handleBlur} onChange={handleChange} />;
});

export default Checkbox;
